import { useState, useEffect } from 'react';
import { listContests, getContestStandings, createContest } from '../../api/clubs';
import type { ContestResponse, ContestStandingsResponse, CreateContestRequest, ClubRole } from '../../types/api';
import { inputClass } from '../ui/inputClass';

interface Props {
  clubId: number;
  role: ClubRole;
}

function statusBadge(status: ContestResponse['status']) {
  switch (status) {
    case 'active':
      return (
        <span className="rounded-full bg-red-100 dark:bg-red-900/30 px-2.5 py-0.5 text-xs font-bold uppercase text-red-600 dark:text-red-400">
          Live
        </span>
      );
    case 'upcoming':
      return (
        <span className="rounded-full bg-teal-100 dark:bg-teal-900/30 px-2.5 py-0.5 text-xs font-semibold text-teal-700 dark:text-teal-400">
          Upcoming
        </span>
      );
    case 'ended':
      return (
        <span className="rounded-full bg-slate-100 dark:bg-slate-700 px-2.5 py-0.5 text-xs font-semibold text-slate-500 dark:text-slate-400">
          Ended
        </span>
      );
  }
}

function countdown(endDate: string): string {
  const diffMs = new Date(endDate).getTime() - Date.now();
  if (diffMs <= 0) return 'ended';
  const totalHours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  return `ends in ${days}d ${hours}h`;
}

function scoringLabel(metric: string): string {
  switch (metric) {
    case 'MostCatches': return 'Most Catches';
    case 'LongestFish': return 'Longest Fish';
    case 'MostSpecies': return 'Most Species';
    default: return metric;
  }
}

interface ContestRowProps {
  clubId: number;
  contest: ContestResponse;
}

function ContestRow({ clubId, contest }: ContestRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [standings, setStandings] = useState<ContestStandingsResponse | null>(null);
  const [loadingStandings, setLoadingStandings] = useState(false);
  const [standingsError, setStandingsError] = useState<string | null>(null);

  function handleToggle() {
    const next = !expanded;
    setExpanded(next);
    if (next && standings === null) {
      setLoadingStandings(true);
      setStandingsError(null);
      getContestStandings(clubId, contest.id)
        .then(setStandings)
        .catch((err: unknown) =>
          setStandingsError(err instanceof Error ? err.message : 'Failed to load standings')
        )
        .finally(() => setLoadingStandings(false));
    }
  }

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
      <button
        onClick={handleToggle}
        className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors cursor-pointer"
      >
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-slate-900 dark:text-slate-100">{contest.name}</span>
          {statusBadge(contest.status)}
          <span className="rounded-full bg-slate-100 dark:bg-slate-700 px-2.5 py-0.5 text-xs font-semibold text-slate-500 dark:text-slate-400">
            {scoringLabel(contest.scoringMetric)}
          </span>
          <span className="text-sm text-slate-500 dark:text-slate-400">
            {contest.status === 'active'
              ? countdown(contest.endDate)
              : contest.status === 'upcoming'
              ? `starts ${new Date(contest.startDate).toLocaleDateString()}`
              : `ended ${new Date(contest.endDate).toLocaleDateString()}`}
          </span>
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`w-4 h-4 shrink-0 text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`}
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>

      {expanded && (
        <div className="border-t border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/30 px-4 py-3">
          {loadingStandings && <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>}
          {standingsError && <p className="text-sm text-red-500">{standingsError}</p>}
          {!loadingStandings && !standingsError && standings && (
            standings.entries.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">No entries yet</p>
            ) : (
              <ol className="space-y-1.5">
                {standings.entries.map((entry) => (
                  <li key={entry.userId} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="w-5 text-right font-bold text-slate-400 dark:text-slate-500">#{entry.rank}</span>
                      <span className="text-slate-800 dark:text-slate-200">{entry.displayName}</span>
                    </div>
                    <span className="text-slate-500 dark:text-slate-400">{entry.value}</span>
                  </li>
                ))}
              </ol>
            )
          )}
        </div>
      )}
    </div>
  );
}

const EMPTY_FORM: CreateContestRequest = {
  name: '',
  startDate: '',
  endDate: '',
  scoringMetric: 'MostCatches',
};

export function ContestsSection({ clubId, role }: Props) {
  const [contests, setContests] = useState<ContestResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<CreateContestRequest>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    listContests(clubId)
      .then(setContests)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load contests'))
      .finally(() => setLoading(false));
  }, [clubId]);

  function handleFormChange(field: keyof CreateContestRequest, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);
    try {
      await createContest(clubId, {
        ...form,
        startDate: form.startDate ? form.startDate + 'T00:00:00Z' : form.startDate,
        endDate: form.endDate ? form.endDate + 'T00:00:00Z' : form.endDate,
      });
      const updated = await listContests(clubId);
      setContests(updated);
      setShowForm(false);
      setForm(EMPTY_FORM);
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Failed to create contest');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-base font-semibold text-slate-900 dark:text-slate-50">Contests</h2>
        {(role === 'Owner' || role === 'Admin') && !showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="rounded-full bg-teal-600 dark:bg-teal-500 px-4 py-1.5 text-sm font-medium text-white hover:bg-teal-700 dark:hover:bg-teal-600 transition-colors cursor-pointer"
          >
            + New Contest
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="mb-5 space-y-3 rounded-xl border border-teal-200 dark:border-teal-800 bg-teal-50 dark:bg-teal-900/20 p-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Name</label>
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              className={inputClass}
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Start date</label>
              <input
                type="date"
                required
                value={form.startDate}
                onChange={(e) => handleFormChange('startDate', e.target.value)}
                className={inputClass}
              />
            </div>
            <div className="flex-1">
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">End date</label>
              <input
                type="date"
                required
                value={form.endDate}
                onChange={(e) => handleFormChange('endDate', e.target.value)}
                className={inputClass}
              />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Scoring metric</label>
            <select
              value={form.scoringMetric}
              onChange={(e) => handleFormChange('scoringMetric', e.target.value)}
              className={inputClass}
            >
              <option value="MostCatches">Most Catches</option>
              <option value="LongestFish">Longest Fish</option>
              <option value="MostSpecies">Most Species</option>
            </select>
          </div>
          {formError && <p className="text-sm text-red-500">{formError}</p>}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={submitting}
              className="rounded-lg bg-teal-600 dark:bg-teal-500 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 dark:hover:bg-teal-600 disabled:opacity-50 transition-colors cursor-pointer"
            >
              {submitting ? 'Creating…' : 'Create Contest'}
            </button>
            <button
              type="button"
              onClick={() => { setShowForm(false); setForm(EMPTY_FORM); setFormError(null); }}
              className="rounded-lg border border-slate-300 dark:border-slate-600 px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading && <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>}
      {error && <p className="text-sm text-red-500">{error}</p>}

      {!loading && !error && contests.length === 0 && (
        <p className="text-sm text-slate-500 dark:text-slate-400">No contests yet</p>
      )}

      {!loading && !error && contests.length > 0 && (
        <div className="space-y-2">
          {contests.map((contest) => (
            <ContestRow key={contest.id} clubId={clubId} contest={contest} />
          ))}
        </div>
      )}
    </div>
  );
}
